/**
 * Order entry: the one place in this client where an HTTP response blocks UI.
 *
 * Everything else renders optimistically or from a stream. The ticket's button
 * resolves on the **POST receipt**, not on the WS `orders` frame, because the
 * receipt has a status code and the frame is a notification — and because
 * `POST /orders` returns the whole order with `status`, `filled_size`,
 * `avg_fill_price_cents` and `fees_paid` already populated (§7.11). A client
 * never learns what its own order did from the socket.
 *
 * ## The exit criterion lives in `#send`
 *
 * > An order placed with the response dropped in flight reconciles to a single
 * > order, never a duplicate.
 *
 * A dropped response is indistinguishable from a rejected request at the socket
 * layer, and the engine may well have the order. §6.3's answer is that the retry
 * must reuse **all three** identifiers — the same `Idempotency-Key`, the same
 * `client_order_id`, the same `nonce_ms`, which means the same signature — so
 * the server replays its stored response instead of matching a second order.
 * Change any one of them and you either get rejected or place a duplicate.
 *
 * That is why `newOrderIdentity()` is called **once, outside the retry loop**,
 * and why the loop re-sends the byte-identical body rather than rebuilding it.
 * The one thing that expires is the nonce: past ~30 s the signature is outside
 * the anti-replay ring (§3.4.3) and cannot be resent at all. §6.3 rule 4 is
 * explicit about what to do then — **abandon and re-price**, because "a
 * 30-second-old order retry is meaningless on a 60-second market" — and this
 * client surfaces exactly that rather than retrying silently into a market the
 * user has not looked at since.
 *
 * ## What it does not do
 *
 * It does not retry a `retryable: false` rejection. §4.4.2: "`retryable` is the
 * server's opinion and it is authoritative. A client that retries a
 * `retryable: false` request is a client that will place a duplicate order."
 */

import type { Api } from '@/api/http/endpoints'
import type { Clock } from '@/lib/clock'
import { HttpError, NetworkError } from '@/api/http/problem'
import type { StoreSet } from '@/stores'
import type { PendingOrder } from '@/stores/pending-orders'
import { presentError, type ErrorAction, type ErrorPresentation } from '@/domain/order-errors'
import { pushToast } from '@/stores/ui'
import { formatAvgCents, formatShares } from '@/lib/format'
import type { IdentityController } from '@/wallet/controller'
import { accountMismatch } from '@/wallet/identity'
import type { OrderDraft } from '@/wallet/order'
import { isNonceFresh, newOrderIdentity, type OrderIdentity } from './ids'

/** Network failures and 5xx only, and only while the nonce is still valid. */
const MAX_ATTEMPTS = 3
const RETRY_BACKOFF_MS = [150, 400]

export type SubmitStatus =
  /** The engine answered and the order exists. `order` is the receipt. */
  | 'PLACED'
  /** The engine answered and refused. Nothing was placed. */
  | 'REJECTED'
  /**
   * The engine may or may not hold it, and the signature can no longer be
   * resent. The client reconciles by `client_order_id` and tells the user to
   * re-check the book — never a silent retry (§6.3 rule 4).
   */
  | 'UNRESOLVED'

export interface SubmitResult {
  status: SubmitStatus
  identity: OrderIdentity
  order?: unknown
  error?: ErrorPresentation
}

export interface OrderControllerOptions {
  api: Api
  clock: Clock
  /** `null` until `/config` has answered — the stores live on the socket. */
  stores: () => StoreSet | null
  /** Loads the wallet chunk on first use. Never on cold load (invariant 13). */
  identity: () => Promise<IdentityController>
  /** Wires `docs/07`'s bracketed affordances to the surfaces that own them. */
  onAction?: (action: ErrorAction) => void
  /** `CONFIG_STALE` asks for a re-read and one resend (§4.3). */
  refreshConfig?: () => Promise<void>
  /** Injected by tests so a retry does not cost real wall-clock seconds. */
  sleep?: (ms: number) => Promise<void>
}

export class OrderController {
  readonly #options: OrderControllerOptions

  /**
   * Submits in flight, by the shape of the order they are placing.
   *
   * **"Two clicks, one order" is a guarantee, so it lives here** rather than in
   * the component that happens to own the button today. `<OrderTicket>` also
   * guards with a ref — that is the affordance, and it stops the second click
   * doing any work at all — but a guarantee that depends on every caller
   * remembering to guard is not a guarantee: Phase 7's roll-gap pre-arm, a
   * keyboard shortcut, and the mobile sheet are all callers this file cannot
   * see.
   *
   * The key is the *logical order*, not "any submit", because two different
   * orders placed a millisecond apart are two orders and the venue is right to
   * take both. Two identical ones overlapping in time are one intent expressed
   * twice, which is exactly what §6.2 promises to collapse — and collapsing them
   * by handing back the *same promise* means the second caller sees the same
   * outcome rather than an error it has to interpret.
   */
  readonly #inFlight = new Map<string, Promise<SubmitResult>>()

  constructor(options: OrderControllerOptions) {
    this.#options = options
  }

  /**
   * Place one order.
   *
   * The optimistic row goes up **before** the signature is requested, not after
   * the POST returns: signing is local and takes single-digit milliseconds, but
   * the taker delay and the round trip are not, and D12 is explicit that the delay must read
   * as normal rather than as a warning. A row that appears only once the engine
   * has answered would make every marketable order look like a stall.
   */
  submit(draft: Omit<OrderDraft, 'clientOrderId' | 'nonceMs' | 'signer' | 'account'>): Promise<SubmitResult> {
    const key = fingerprintOf(draft)
    const existing = this.#inFlight.get(key)
    // Two clicks, one order. The second caller waits on the first one's result
    // instead of minting a second `client_order_id` — which would be a second
    // logical order (§6.3), and the engine would be right to place it.
    if (existing) return existing

    const running = this.#submit(draft).finally(() => {
      if (this.#inFlight.get(key) === running) this.#inFlight.delete(key)
    })
    this.#inFlight.set(key, running)
    return running
  }

  async #submit(draft: Omit<OrderDraft, 'clientOrderId' | 'nonceMs' | 'signer' | 'account'>): Promise<SubmitResult> {
    const identity = await this.#options.identity()
    const state = identity.store.state
    const account = state.address
    const signer = state.keyAddress
    if (!account || !signer) {
      // The ticket gates on `ticketGate()` and cannot normally get here; this is
      // the race where a key is revoked between the render and the click.
      const error = presentError(new Error('Enable trading to place an order.'))
      this.#toast(error)
      return { status: 'REJECTED', identity: newOrderIdentity(this.#options.clock.now()), error }
    }

    // **`account` is about to become the order's `maker`, so this is where the
    // comparison has to be** — `docs/17`. `ticketGate()` shows `WRONG_ACCOUNT`
    // and `validateOrder` blocks the button, but a gate that lives only in the
    // UI is one a future caller reaches around, and this repository's audit is
    // mostly that shape: a correct check with a path around it. Refusing here
    // costs one comparison per order and closes the class.
    //
    // The wallet moving is not an error the client can resolve on the user's
    // behalf. Signing them back in silently would pick one of the two accounts
    // for them, and placing the order under the session's address is exactly
    // the behaviour this refuses.
    const mismatch = accountMismatch(state)
    if (mismatch) {
      const error = presentError(
        new Error(
          'Your wallet is on a different account than the one you signed in with. Sign in again to trade as it.',
        ),
      )
      this.#toast(error)
      return { status: 'REJECTED', identity: newOrderIdentity(this.#options.clock.now()), error }
    }

    const ids = newOrderIdentity(this.#options.clock.now())
    const full: OrderDraft = { ...draft, account, signer, clientOrderId: ids.clientOrderId, nonceMs: ids.nonceMs }

    this.#addPending(full)

    try {
      return await this.#signAndSend(identity, full, ids)
    } catch (error) {
      const presentation = presentError(error)
      this.#retire(ids.clientOrderId)
      this.#toast(presentation)
      return { status: 'REJECTED', identity: ids, error: presentation }
    }
  }

  /**
   * Cancel one resting order.
   *
   * `ORDER_NOT_CANCELLABLE` is **treated as success** (§7.5): the user asked for
   * the order not to be resting, and it is not resting. Surfacing a red error
   * because it filled 4 ms before the click is technically accurate and
   * practically wrong. Cancels take the session cookie alone and carry no
   * signature — a cancel can only reduce risk (D3 scope), and a user whose key
   * expired mid-market must still be able to pull their orders.
   */
  async cancel(orderId: string): Promise<boolean> {
    try {
      const result = await this.#options.api.trading.cancelOrder(orderId)
      this.#applyReceipt(result)
      return true
    } catch (error) {
      if (error instanceof HttpError && error.problem?.code === 'ORDER_NOT_CANCELLABLE') {
        // Reconcile from the truth rather than from the error: `meta.status`
        // says what actually happened to it, and the row should show that.
        void this.#reconcileOrderId(orderId)
        return true
      }
      const presentation = presentError(error)
      if (error instanceof HttpError && error.problem?.code === 'ORDER_NOT_FOUND') {
        void this.#reconcileOrderId(orderId)
        return true
      }
      this.#toast(presentation)
      return false
    }
  }

  /**
   * Cancel every resting order, optionally in one market.
   *
   * `200` with a per-order breakdown, never a 4xx: "one of the eleven had
   * already filled" is not a failure of the request (§7.5). Everything in
   * `not_cancelled` is reconciled rather than reported — the user asked for none
   * of them to be resting and none of them is.
   *
   * **`not_cancelled` is a map, not an array**, and the two normative documents
   * disagree about that: `openapi` types it `object` with
   * `additionalProperties` — "order id to reason code" — while `docs/04` §7.5
   * shows `not_cancelled: [{order_id, code, status}]` alongside a
   * `not_cancelled_count` that `openapi` does not define. §F1 gives object
   * shapes to `openapi`, so the map wins. Register §F22h.
   */
  async cancelAll(marketId?: string): Promise<number> {
    try {
      const result = (await this.#options.api.trading.cancelOrders(
        (marketId ? { market_id: marketId } : {}) as never,
      )) as { cancelled?: string[]; not_cancelled?: Record<string, unknown>; cancelled_count?: number }

      for (const id of result.cancelled ?? []) void this.#reconcileOrderId(id)
      for (const id of Object.keys(result.not_cancelled ?? {})) void this.#reconcileOrderId(id)
      return result.cancelled_count ?? result.cancelled?.length ?? 0
    } catch (error) {
      this.#toast(presentError(error))
      return 0
    }
  }

  // -------------------------------------------------------------------------

  async #signAndSend(
    identity: IdentityController,
    draft: OrderDraft,
    ids: OrderIdentity,
  ): Promise<SubmitResult> {
    // The signing stack arrives here and nowhere earlier. `wallet/order.ts`
    // reaches `viem`'s `keccak256`, so a static import of it from the ticket
    // would put the signing stack on every anonymous cold load (§F21k).
    const { toOrderTypedData, orderRequestBody } = await import('@/wallet/order')

    const { signature, keyAddress } = await identity.keys.signTypedData(toOrderTypedData(draft))
    const body = orderRequestBody(draft)

    // The three headers of §3.4.2. `X-UD-Nonce` **must equal `message.nonce_ms`
    // exactly** — a mismatch is `SIGNATURE_INVALID`, not `NONCE_REUSED`, because
    // it means the body and the header disagree about what was signed.
    const options = {
      idempotencyKey: ids.idempotencyKey,
      signature: { key: keyAddress, signature, nonce: String(ids.nonceMs) },
    }

    return this.#send(draft, ids, body, options, identity)
  }

  /**
   * The retry loop, and the only place a `POST /orders` is issued.
   *
   * Every attempt sends the **same bytes**: same body, same `Idempotency-Key`,
   * same signature, same nonce. The server's first attempt either executed or it
   * did not; a replay of an identical body returns the stored response with
   * `Idempotency-Replayed: true` and never touches the engine (§6.2).
   */
  async #send(
    draft: OrderDraft,
    ids: OrderIdentity,
    body: Record<string, unknown>,
    options: { idempotencyKey: string; signature: { key: string; signature: string; nonce: string } },
    identity: IdentityController,
  ): Promise<SubmitResult> {
    let lastError: unknown = null

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        // The signature, not the request, is what expires. Past the ring's
        // window the bytes are unsendable and §6.3 rule 4 says to abandon.
        if (!isNonceFresh(ids.nonceMs, this.#options.clock.now())) break
        await this.#sleep(RETRY_BACKOFF_MS[attempt - 1] ?? 400)
      }

      try {
        const order = await this.#options.api.trading.placeOrder(body as never, options as never)
        this.#applyReceipt(order)
        // Retired by the id **this client submitted**, not by the one the
        // payload happens to echo. `#applyOrder` also retires from the payload,
        // and that covers the WS frame — but a receipt that arrived without the
        // field would strand the optimistic row on screen forever, and the one
        // thing always known here is what was asked for.
        this.#retire(ids.clientOrderId)
        this.#announceFill(order)
        return { status: 'PLACED', identity: ids, order }
      } catch (error) {
        lastError = error

        // A dropped response. The engine may hold the order; the only safe move
        // is to send the identical request again and let idempotency decide.
        if (error instanceof NetworkError && !error.aborted) continue

        if (error instanceof HttpError) {
          // §6.2: a 5xx is **not** stored, so a replay genuinely re-executes —
          // and §4.3 is explicit that a `POST /orders` which returned 500 "must
          // be resolved by replaying the same `Idempotency-Key`, never by
          // sending a fresh order".
          if (error.status >= 500) continue

          // The key is in flight on another attempt or another tab. Wait out
          // `Retry-After` and replay; never send a fresh key (§6.2).
          if (error.problem?.code === 'IDEMPOTENCY_KEY_REUSED' && error.retryable) {
            await this.#sleep(error.retryAfterMs ?? 1_000)
            continue
          }

          const handled = await this.#handleRejection(error, identity)
          if (handled === 'RETRY') continue
        }
        break
      }
    }

    return this.#resolveUnfinished(draft, ids, lastError)
  }

  /**
   * The rejections that mean something other than "stop".
   *
   * Deliberately short. Everything not listed here is a rejection the user has
   * to act on, and re-sending it would be the client second-guessing
   * `retryable: false`.
   */
  async #handleRejection(error: HttpError, identity: IdentityController): Promise<'RETRY' | 'STOP'> {
    const code = error.problem?.code

    if (code === 'CONFIG_STALE' && this.#options.refreshConfig) {
      // §4.3: re-fetch `/config` with `no-cache`, re-derive, resend once. The
      // resend is the loop's next attempt, with the same three identifiers.
      await this.#options.refreshConfig()
      return 'RETRY'
    }

    if (code === 'SESSION_KEY_EXPIRED' || code === 'SESSION_KEY_INVALID' || code === 'SESSION_KEY_REVOKED') {
      // Drop the local key so `ticketGate()` moves to `ENABLE_TRADING` and the
      // ticket asks for one signature. Deliberately *not* a popup raised from
      // inside a submit: docs/03 §3.2 puts arming on ticket focus, never
      // between an intent and its execution.
      await identity.keys.forgetLocal()
      identity.store.dropKey('Your trading key is no longer valid. Enable trading again to continue.')
      return 'STOP'
    }

    return 'STOP'
  }

  /**
   * Nothing conclusive came back. Find out what actually happened.
   *
   * `GET /orders?client_order_id=…` is the *other* way to resolve a lost POST —
   * the one for when the client never got an `order_id` back at all (docs/03
   * §3.5), and the reason `client_order_id` is mandatory on every order.
   */
  async #resolveUnfinished(draft: OrderDraft, ids: OrderIdentity, lastError: unknown): Promise<SubmitResult> {
    const found = await this.#lookupByClientOrderId(ids.clientOrderId)
    if (found) {
      this.#applyReceipt(found)
      this.#retire(ids.clientOrderId)
      return { status: 'PLACED', identity: ids, order: found }
    }

    this.#retire(ids.clientOrderId)

    // The signature aged out mid-retry: the order was never placed and cannot
    // now be. §6.3 rule 4 asks for exactly this sentence rather than a silent
    // re-sign — the book has moved and the user should see it before committing
    // again.
    const stale = !isNonceFresh(ids.nonceMs, this.#options.clock.now())
    const presentation: ErrorPresentation =
      stale ?
        {
          code: 'ORDER_ABANDONED',
          kind: 'warning',
          title: 'Too late to retry',
          body: `That ${draft.outcome} order never reached the exchange and its signature has expired. Check the book and re-price.`,
          unknown: false,
        }
      : presentError(lastError)

    this.#toast(presentation)
    return { status: 'UNRESOLVED', identity: ids, error: presentation }
  }

  async #lookupByClientOrderId(clientOrderId: string): Promise<unknown | null> {
    try {
      const page = (await this.#options.api.trading.listOrders({ client_order_id: clientOrderId } as never)) as {
        data?: unknown[]
      }
      return page.data?.[0] ?? null
    } catch {
      // A failed reconciliation is not evidence of anything. The pending row is
      // retired and the user is told to re-check — which is the honest answer
      // when the client genuinely does not know.
      return null
    }
  }

  async #reconcileOrderId(orderId: string): Promise<void> {
    try {
      this.#applyReceipt(await this.#options.api.trading.getOrder(orderId))
    } catch {
      /* the `orders` channel will deliver it; this is only the faster path */
    }
  }

  // -------------------------------------------------------------------------

  #addPending(draft: OrderDraft): void {
    const stores = this.#options.stores()
    if (!stores) return
    const row: PendingOrder = {
      clientOrderId: draft.clientOrderId,
      marketId: draft.marketId,
      outcome: draft.outcome,
      side: draft.side,
      orderType: draft.orderType,
      priceCents: draft.priceCents,
      size: formatMicroShares(draft.sizeShares),
      createdAtMs: this.#options.clock.now(),
    }
    stores.pendingOrders.upsert(row)
    // Notified directly, not through the rAF flush: a click is not a frame of
    // market data and there is nothing to coalesce it with (`stores/ui.ts` makes
    // the same call for the same reason).
    stores.pendingOrders.store.notify()
  }

  #retire(clientOrderId: string): void {
    const stores = this.#options.stores()
    if (!stores) return
    if (stores.retirePending(clientOrderId)) stores.pendingOrders.store.notify()
  }

  #applyReceipt(order: unknown): void {
    const stores = this.#options.stores()
    if (!stores) return
    stores.applyOrderReceipt(order)
    stores.orders.store.notify()
    stores.pendingOrders.store.notify()
  }

  /**
   * The one toast a *successful* order produces, and only when it filled.
   *
   * A resting order gets no toast: it is already on screen in Open Orders, and a
   * notification that duplicates a row the user is looking at is noise. A fill
   * is the moment the product did the thing that was asked, and is the one place
   * `ease-spring` is used (components.md §3).
   */
  #announceFill(order: unknown): void {
    const row = order as Record<string, unknown> | null
    if (!row) return
    const filled = typeof row.filled_size === 'string' ? row.filled_size : '0'
    if (parseFloatFree(filled) === 0) return

    const price = typeof row.avg_fill_price_cents === 'string' ? row.avg_fill_price_cents : null
    const outcome = typeof row.outcome === 'string' ? row.outcome : ''
    pushToast({
      tone: 'success',
      title: 'Filled',
      ...(price ? { detail: `${formatShares(filled)} ${outcome} @ ${formatAvgCents(price)}` } : {}),
      ...(typeof row.market_id === 'string' ? { body: row.market_id } : {}),
    })
  }

  /** `quiet` never reaches the stack — it renders inline or drives a retry. */
  #toast(presentation: ErrorPresentation): void {
    if (presentation.kind === 'quiet') return
    const action = presentation.action
    pushToast({
      tone: presentation.kind === 'error' ? 'danger' : presentation.kind,
      title: presentation.title,
      body: presentation.body,
      ...(presentation.detail ? { detail: presentation.detail } : {}),
      ...(action && this.#options.onAction
        ? { action: { label: LABEL_FOR_ACTION[action], run: () => this.#options.onAction?.(action) } }
        : {}),
    })
  }

  #sleep(ms: number): Promise<void> {
    return this.#options.sleep ? this.#options.sleep(ms) : new Promise((r) => globalThis.setTimeout(r, ms))
  }
}

// ---------------------------------------------------------------------------

/**
 * What makes two submits "the same order".
 *
 * Every field the user chose, and nothing they did not: no timestamp, no nonce,
 * no identity — those are minted *after* this, and including them would make
 * every submit unique, which is the bug this exists to prevent.
 */
function fingerprintOf(draft: Omit<OrderDraft, 'clientOrderId' | 'nonceMs' | 'signer' | 'account'>): string {
  return [
    draft.marketId,
    draft.outcome,
    draft.side,
    draft.orderType,
    draft.timeInForce,
    draft.priceCents ?? 'MKT',
    draft.sizeShares.toString(),
    draft.postOnly ? 'PO' : '',
    draft.reduceOnly ? 'RO' : '',
  ].join('|')
}

const LABEL_FOR_ACTION: Record<ErrorAction, string> = {
  SIGN_IN: 'Sign in',
  ENABLE_TRADING: 'Enable trading',
  CANCEL_ALL: 'Cancel all',
  SUPPORT: 'Contact support',
}

function formatMicroShares(micro: bigint): string {
  const whole = micro / 1_000_000n
  return `${whole}.${(micro % 1_000_000n).toString().padStart(6, '0')}`
}

/**
 * "Is this decimal string non-zero" without a float going near a balance.
 *
 * Invariant 6 bans a float touching money, and this is not touching money — it
 * is answering a yes/no about a string. Done by inspection rather than by
 * `Number()` so the rule needs no exception: `"0.000000"` is zero, anything with
 * a non-zero digit is not.
 */
function parseFloatFree(value: string): 0 | 1 {
  return /[1-9]/.test(value) ? 1 : 0
}
